import { Form, Input, message, Modal, Typography } from 'antd';
import type { SolicitudResponse } from '../../../api/types';
import { useT } from '../../../locales';
import { useRejectSolicitud } from '../api/useSolicitudes';

const { TextArea } = Input;

interface RejectSolicitudModalProps {
  solicitudId: number;
  open: boolean;
  onClose: () => void;
  solicitud?: SolicitudResponse;
}

interface RejectFormValues {
  motivo: string;
}

export function RejectSolicitudModal({
  solicitudId,
  open,
  onClose,
  solicitud,
}: RejectSolicitudModalProps) {
  const t = useT();
  const [form] = Form.useForm<RejectFormValues>();
  const reject = useRejectSolicitud();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: RejectFormValues) => {
    try {
      await reject.mutateAsync({ id: solicitudId, motivo: values.motivo.trim() });
      message.success(t('pages.solicitudes.rejectSuccess'));
      handleCancel();
    } catch {
      message.error(t('pages.solicitudes.rejectError'));
    }
  };

  return (
    <Modal
      title={t('pages.solicitudes.rejectTitle')}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={reject.isPending}
      destroyOnHidden
      okButtonProps={{ danger: true }}
      okText={t('pages.solicitudes.reject')}
      cancelText={t('common.cancel')}
      centered
      width={520}
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) form.resetFields();
      }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {solicitud && <Typography.Paragraph type="secondary">{solicitud.email}</Typography.Paragraph>}
      <Form<RejectFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        clearOnDestroy
        onFinish={handleFinish}
      >
        <Form.Item
          name="motivo"
          label={t('pages.solicitudes.reasonLabel')}
          required
          rules={[
            {
              validator: (_, value: string | undefined) =>
                typeof value === 'string' && value.trim().length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('pages.solicitudes.reasonRequired'))),
            },
          ]}
        >
          <TextArea rows={3} placeholder={t('pages.solicitudes.reasonPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
